"use client";
import { Suspense, useCallback, useEffect, useState } from "react";
import { Skeleton, useMediaQuery } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { signIn, useSession } from "next-auth/react";
import MenuButton from "./menuButton";
import { useAcceptTermsAndConditions } from "@/api/traveler";
import { useTranslation } from "@/app/i18n/client";
import esp_flag from "@/assets/images/icons/esp_flag.svg";
import uk_flag from "@/assets/images/icons/uk_flag.svg";
import vee_logo_large from "@/assets/images/logos/vee-logo-png.png";
import HexagonButton from "@/common/customElements/HexagonButton";
import { CustomDiv, InlineDiv, VerticalCenterDiv } from "@/common/divs";
import { TextLink } from "@/common/texts";
import { ProfileButton } from "@/components/layout/header/profileButton";
import ModalTermsAndConditions from "@/components/login/modalTermsAndConditions";
import useAuth from "@/hooks/useAuth";
import { useFixHydration } from "@/hooks/useFixHydration";
import { NEUTRO_TITLES_COLOR, PRIMARY_BUTTON_COLOR } from "@/utils/constants";
import routes from "@/utils/routes.json";

const HeaderLayout: React.FC = () => {
  const { data: session, update, status } = useSession();
  useAuth(); //check if user is authenticated and redirect to login if not
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const isMobile = useMediaQuery("(max-width: 930px)");
  const { mutateAsync: acceptTerms } = useAcceptTermsAndConditions();
  useEffect(() => {
    if (session) {
      if (session.user) {
        if (session.user.tycAccepted) {
          //@ts-ignore
          if (session.user.tycAccepted !== "true") {
            if (session.user.tycAccepted !== true) {
              setOpenTerms(true);
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);
  const pathName = usePathname();
  const changeLanguageInSameRoute = (lng: string) => {
    const splitParams = window.location.href.split("?");
    if (splitParams.length > 1) {
      const split = pathName.split("/");
      split[1] = lng;
      router.push(split.join("/") + "?" + splitParams[1]);
      if (split[2] == "activity") {
        setTimeout(() => {
          location.reload();
        }, 1000);
      }
    } else {
      const split = pathName.split("/");
      split[1] = lng;
      router.push(split.join("/"));
      if (split[2] == "activity") {
        setTimeout(() => {
          location.reload();
        }, 1000);
      }
    }
  };
  const [openTerms, setOpenTerms] = useState(false);

  const handleAcceptTermsAndConditions = useCallback(async () => {
    const res = await acceptTerms();
    if (res.status === "SUCCESS") {
      await update();
      setOpenTerms(false);
    }
  }, [acceptTerms, update]);

  const mounted = useFixHydration();

  if (!mounted) {
    return null;
  }

  return (
    <Suspense>
      <header>
        <InlineDiv
          className="responsiveHeader"
          width="100%"
          height="80px"
          background={NEUTRO_TITLES_COLOR}
          paddingX="108px"
        >
          <VerticalCenterDiv className="responsiveHeader">
            <Link
              href="/home"
              style={{
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
              }}
            >
              <Image
                src={vee_logo_large}
                alt="logo"
                width={123}
                height={46}
                className="h-auto"
                quality={100}
                priority={true}
              />
            </Link>
          </VerticalCenterDiv>
          <VerticalCenterDiv
            className="hideOnResponsive"
            right="108px"
            style={{ display: "flex", gap: "16px", alignItems: "center" }}
          >
            {routes.map((route, index) => {
              return (
                <Link
                  href={
                    i18n.language == "en" ?
                      route["en-route"]
                    : route["es-route"]
                  }
                  key={index}
                >
                  <TextLink>{t(route.i18_tag)}</TextLink>
                </Link>
              );
            })}
            <InlineDiv>
              <HexagonButton
                width="35pX"
                marginX="5px"
                backgroundColor={PRIMARY_BUTTON_COLOR}
                onClick={() => {
                  changeLanguageInSameRoute("es");
                }}
              >
                {" "}
                <Image
                  alt="spain"
                  width="18"
                  height="12"
                  src={esp_flag.src}
                  style={{
                    position: "absolute",
                    top: "30%",
                    left: "24%",
                    height: "12px",
                    width: "18px",
                  }}
                />
              </HexagonButton>
              <HexagonButton
                width="35pX"
                marginX="5px"
                backgroundColor={PRIMARY_BUTTON_COLOR}
                onClick={() => {
                  changeLanguageInSameRoute("en");
                  //router.push("/en/");
                }}
              >
                {" "}
                <Image
                  alt="uk"
                  width="18"
                  height="12"
                  src={uk_flag.src}
                  style={{
                    position: "absolute",
                    top: "30%",
                    left: "24%",
                    height: "12px",
                    width: "18px",
                  }}
                />
              </HexagonButton>
            </InlineDiv>
            {status === "loading" ?
              <Skeleton width={50} />
            : session?.user ?
              <ProfileButton />
            : <TextLink
                onClick={() => {
                  signIn();
                }}
              >
                {t("first_entry.login")}
              </TextLink>
            }
          </VerticalCenterDiv>

          {isMobile && (
            <VerticalCenterDiv style={{ right: "0" }}>
              <InlineDiv>
                {isMobile && (
                  <CustomDiv
                    style={{
                      display: "flex",
                      gap: "16px",
                      alignItems: "center",
                      marginRight: "10px",
                    }}
                  >
                    <InlineDiv>
                      <HexagonButton
                        width="35pX"
                        marginX="5px"
                        backgroundColor={PRIMARY_BUTTON_COLOR}
                        onClick={() => {
                          changeLanguageInSameRoute("es");
                        }}
                      >
                        {" "}
                        <Image
                          alt="spain"
                          width="18"
                          height="12"
                          src={esp_flag.src}
                          style={{
                            position: "absolute",
                            top: "30%",
                            left: "24%",
                            height: "12px",
                            width: "18px",
                          }}
                        />
                      </HexagonButton>
                      <HexagonButton
                        width="35pX"
                        marginX="5px"
                        backgroundColor={PRIMARY_BUTTON_COLOR}
                        onClick={() => {
                          changeLanguageInSameRoute("en");
                          //router.push("/en/");
                        }}
                      >
                        {" "}
                        <Image
                          alt="uk"
                          width="18"
                          height="12"
                          src={uk_flag.src}
                          style={{
                            position: "absolute",
                            top: "30%",
                            left: "24%",
                            height: "12px",
                            width: "18px",
                          }}
                        />
                      </HexagonButton>
                    </InlineDiv>
                  </CustomDiv>
                )}
                {session?.user ?
                  <ProfileButton />
                : <TextLink
                    style={{ paddingRight: "15px", alignSelf: "center" }}
                    onClick={() => {
                      signIn();
                    }}
                  >
                    {t("first_entry.login")}
                  </TextLink>
                }
                <MenuButton />
              </InlineDiv>
            </VerticalCenterDiv>
          )}
        </InlineDiv>
        {openTerms && (
          <ModalTermsAndConditions
            open={openTerms}
            allowClosing={false}
            setOpen={setOpenTerms}
            actionToDo={handleAcceptTermsAndConditions}
          />
        )}
      </header>
    </Suspense>
  );
};

export default HeaderLayout;
